import uniqBy from "lodash/uniqBy";

import { z } from "@reflag/shared/utils/zod";

import { APIHandlerError } from "./api";
import {
  AttributeField,
  AttributeFilter as OldAttributeFilter,
  AttributeFilterOperator,
  attributeFilterOperatorsList,
  AttributeFilterType,
  FIRST_SEEN_FIELD_KEY,
  isNumeric,
  LAST_SEEN_FIELD_KEY,
  listOperators,
  USER_COUNT_FIELD_KEY,
  numberOperators,
  operatorsWithoutValue,
  relevantOperators,
  systemAttributeFieldKeys,
  userSystemAttributeFieldKeys,
} from "./attributeFilter";
import { FunnelStep, FunnelStepList } from "./featureAPI";
import { FeatureTargetingSchema } from "./featureTargetingAPI";
import { SegmentDTO } from "./segmentAPI";
import { filterMap } from "./utils/array";

export type AdjustAttributeFieldsCallback = (
  filterType: UIFilterType,
  fields: AttributeField[],
) => AttributeField[];

export const COMPANY_ID_CONTEXT_FIELD = "company.id";
export const USER_ID_CONTEXT_FIELD = "user.id";
export type RolloutContextAttribute =
  | typeof COMPANY_ID_CONTEXT_FIELD
  | typeof USER_ID_CONTEXT_FIELD;

export function normalizeRolloutContextAttribute(
  attribute: string | null | undefined,
): RolloutContextAttribute {
  return attribute === USER_ID_CONTEXT_FIELD
    ? USER_ID_CONTEXT_FIELD
    : COMPANY_ID_CONTEXT_FIELD;
}

export const MAX_ROLLOUT_THRESHOLD = 100000;

export const featureMetrics = [
  "funnelStep",
  "frequency",
  "satisfaction",
  "eventCount",
  "firstUsed",
  "lastUsed",
  "firstExposureAt",
  "lastExposureAt",
  "exposureCount",
  "usageCount",
] as const;
export const FeatureMetricSchema = z.enum(featureMetrics);
export type FeatureMetric = z.infer<typeof FeatureMetricSchema>;

export const featureMetricOperators: Record<
  FeatureMetric,
  readonly AttributeFilterOperator[]
> = {
  funnelStep: ["IS", "IS_NOT", "ANY_OF", "NOT_ANY_OF", "GT", "LT"],
  frequency: ["IS", "IS_NOT", "GT", "LT", "ANY_OF", "NOT_ANY_OF"],
  satisfaction: ["IS", "IS_NOT", "GT", "LT", "ANY_OF", "NOT_ANY_OF"],
  eventCount: ["IS", "IS_NOT", "GT", "LT"],
  firstUsed: ["AFTER", "BEFORE", "DATE_AFTER", "DATE_BEFORE"],
  lastUsed: ["AFTER", "BEFORE", "DATE_AFTER", "DATE_BEFORE"],
  firstExposureAt: ["AFTER", "BEFORE", "DATE_AFTER", "DATE_BEFORE"],
  lastExposureAt: ["AFTER", "BEFORE", "DATE_AFTER", "DATE_BEFORE"],
  exposureCount: ["IS", "IS_NOT", "GT", "LT"],
  usageCount: ["IS", "IS_NOT", "GT", "LT"],
} as const;

export const featureMetricOperatorRefinement = (
  {
    metric,
    operator,
  }: {
    metric?: FeatureMetric | undefined;
    operator?: AttributeFilterOperator | undefined;
  },
  ctx: z.RefinementCtx,
) => {
  if (
    metric &&
    operator &&
    !featureMetricOperators[metric].includes(operator)
  ) {
    ctx.addIssue({
      code: "custom",
      message: `The operator is not supported`,
      path: ["operator"],
    });
  }
};

export const operatorRefinement = (
  {
    type,
    field,
    operator,
    values,
  }: {
    type: UIFilterType;
    field?: string;
    operator?: AttributeFilterOperator | "and" | "or" | undefined;
    values?: string[] | undefined;
  },
  ctx: z.RefinementCtx,
) => {
  // Ignore group operators
  if (operator === "and" || operator === "or") {
    return;
  }

  if (type === "segment" || type === "featureTargeting") {
    return;
  }

  if (
    type === "companyAttribute" &&
    field === USER_COUNT_FIELD_KEY &&
    operator &&
    ![
      "IS",
      "IS_NOT",
      "GT",
      "LT",
      "ANY_OF",
      "NOT_ANY_OF",
      "SET",
      "NOT_SET",
    ].includes(operator)
  ) {
    ctx.addIssue({
      code: "custom",
      message: "Unsupported operator for user count",
      path: ["operator"],
    });
  }

  if (operator && listOperators.includes(operator) && !values?.length) {
    ctx.addIssue({
      code: "custom",
      message: "The list must not be empty",
      path: ["values"],
    });
  }

  const value = values?.[0];

  if (operator && numberOperators.includes(operator) && !isNumeric(value)) {
    ctx.addIssue({
      code: "custom",
      message: "The value must be a number",
      path: ["values", 0],
    });
  }

  if (
    operator &&
    (value === "" || typeof value === "undefined") &&
    !operatorsWithoutValue.includes(operator)
  ) {
    ctx.addIssue({
      code: "custom",
      message: "A value must be set",
      path: ["values", 0],
    });
  }
};

/** Internal use before superRefine */
export const BaseCompanyAttributeFilterSchema = z.object({
  type: z.literal("companyAttribute"),
  field: z
    .string({ error: "An attribute must be selected" })
    .min(1, "An attribute must be selected"),
  operator: z.enum(attributeFilterOperatorsList),
  values: z.array(z.string()).optional(),
});

export type CompanyAttributeFilter = z.infer<
  typeof BaseCompanyAttributeFilterSchema
>;

/** Internal use before superRefine */
export const CompanyIdsFilterSchema = z.object({
  type: z.literal("companyIds"),
  ids: z.array(z.string()),
});
export type CompanyIdsFilter = z.infer<typeof CompanyIdsFilterSchema>;

export const UserIdsFilterSchema = z.object({
  type: z.literal("userIds"),
  ids: z.array(z.string()),
});
export type UserIdsFilter = z.infer<typeof UserIdsFilterSchema>;

/** Internal use before superRefine */
export const BaseCompanyFeatureMetricFilterSchema = z.object({
  type: z.literal("featureMetric"),
  featureId: z
    .string({ error: "A flag must be selected" })
    .min(1, "A flag must be selected"),
  metric: FeatureMetricSchema,
  operator: z.enum(attributeFilterOperatorsList),
  values: z.array(z.string()),
});

export type CompanyFeatureMetricFilter = z.infer<
  typeof BaseCompanyFeatureMetricFilterSchema
>;

// Special case as infer is not allowed for recursive schemas
export type FilterGroup<T extends FilterClass> = {
  type: "group";
  operator: "and" | "or";
  filters?: FilterTree<T>[];
};

export const FilterGroupSchema = z.object({
  type: z.literal("group"),
  filters: z.lazy(() => z.array(UIFilterSchema)).optional(),
  operator: z.enum(["and", "or"]),
});

export const FeatureTargetingFilterSchema = z.object({
  type: z.literal("featureTargeting"),
  operator: z.enum(["FLAG", "NOT_FLAG"]),
  featureId: z.string().min(1),
});

export type FeatureTargetingFilter = z.infer<
  typeof FeatureTargetingFilterSchema
> & {
  feature?: {
    id: string;
    name: string;
    key: string;
  };
};

export const SegmentFilterSchema = z.object({
  type: z.literal("segment"),
  operator: z.enum(["SEGMENT", "NOT_SEGMENT"]),
  segmentId: z.string().min(1),
});

export type SegmentFilter = z.infer<typeof SegmentFilterSchema> & {
  segment?: {
    id: string;
    name: string;
    deletedAt?: Date | null;
  };
};

export const FeatureFlagRolloutFilterSchema = z.object({
  type: z.literal("featureFlagRolloutPercentage"),
  flagKey: z.string(),
  percentage: z.number(),
  partialRolloutContextAttribute: z.string().optional(),
});

export type FeatureFlagRolloutPercentageFilter = z.infer<
  typeof FeatureFlagRolloutFilterSchema
>;

export const OtherContextFilterSchema = BaseCompanyAttributeFilterSchema.extend(
  {
    type: z.literal("otherContext"),
  },
);

export type OtherContextFilter = z.infer<typeof OtherContextFilterSchema>;

export const UserAttributeFilterSchema =
  BaseCompanyAttributeFilterSchema.extend({
    type: z.literal("userAttribute"),
  });

export type UserAttributeFilter = z.infer<typeof UserAttributeFilterSchema>;

export type FilterClass =
  | SegmentFilter
  | CompanyAttributeFilter
  | CompanyIdsFilter
  | UserIdsFilter
  | CompanyFeatureMetricFilter
  | FilterGroup<any>
  | FilterNegation<any>
  | FilterConstant
  | FeatureFlagRolloutPercentageFilter
  | OtherContextFilter
  | UserAttributeFilter
  | FeatureTargetingFilter;

export type FilterNegation<T extends FilterClass> = {
  type: "negation";
  filter: FilterTree<T>;
};

export type FilterConstant = {
  type: "constant";
  value: boolean;
};

export type FilterTree<T extends FilterClass> =
  | FilterGroup<T>
  | FilterNegation<T>
  | T;

// These are recognized by the UI
export type SingleUIFilter =
  | CompanyAttributeFilter
  | CompanyIdsFilter
  | UserIdsFilter
  | UserAttributeFilter
  | OtherContextFilter
  | CompanyFeatureMetricFilter
  | SegmentFilter
  | FeatureTargetingFilter
  | FeatureFlagRolloutPercentageFilter;

export type UIFilter = FilterTree<SingleUIFilter>;
export type UIFilterType = UIFilter["type"];

export const UIFilterSchema: z.ZodType<UIFilter, UIFilter> = z
  .discriminatedUnion("type", [
    BaseCompanyAttributeFilterSchema,
    BaseCompanyFeatureMetricFilterSchema,
    CompanyIdsFilterSchema,
    UserIdsFilterSchema,
    SegmentFilterSchema,
    OtherContextFilterSchema,
    UserAttributeFilterSchema,
    FilterGroupSchema,
    FeatureTargetingFilterSchema,
    FeatureFlagRolloutFilterSchema,
  ])
  .superRefine(featureMetricOperatorRefinement as any)
  .superRefine(operatorRefinement);

export const SingleUIFilterSchema: z.ZodType<SingleUIFilter, SingleUIFilter> = z
  .discriminatedUnion("type", [
    BaseCompanyAttributeFilterSchema,
    BaseCompanyFeatureMetricFilterSchema,
    CompanyIdsFilterSchema,
    UserIdsFilterSchema,
    UserAttributeFilterSchema,
    OtherContextFilterSchema,
    SegmentFilterSchema,
    FeatureTargetingFilterSchema,
    FeatureFlagRolloutFilterSchema,
  ])
  .superRefine(featureMetricOperatorRefinement as any)
  .superRefine(operatorRefinement);

// Scope authoring guidance to flag clauses without changing general filters.
const flagAttributeFields = {
  field: BaseCompanyAttributeFilterSchema.shape.field.describe(
    "Exact attribute key within the context selected by type, not a display label or a guessed context path: e.g. plan for companyAttribute, role for userAttribute, or region for otherContext.",
  ),
  operator: BaseCompanyAttributeFilterSchema.shape.operator.describe(
    "Comparison operator appropriate for the attribute. ANY_OF/NOT_ANY_OF require a nonempty values list; GT/LT require numeric strings. SET/NOT_SET test presence and IS_TRUE/IS_FALSE test booleans without values.",
  ),
  values: BaseCompanyAttributeFilterSchema.shape.values.describe(
    'Comparison values encoded as strings, including numbers (e.g. ["25"]) and booleans (e.g. ["true"]). Provide values for operators that require them; omit for SET, NOT_SET, IS_TRUE, and IS_FALSE.',
  ),
};

export const SingleFlagFilterSchema = z
  .discriminatedUnion("type", [
    BaseCompanyAttributeFilterSchema.extend(flagAttributeFields).strict(),
    UserAttributeFilterSchema.extend(flagAttributeFields).strict(),
    OtherContextFilterSchema.extend(flagAttributeFields).strict(),
    SegmentFilterSchema.extend({
      segmentId: SegmentFilterSchema.shape.segmentId.describe(
        "Internal Reflag segment ID from the app tool, not a segment name or SDK company/user ID.",
      ),
      operator: SegmentFilterSchema.shape.operator.describe(
        "SEGMENT matches membership in the referenced segment; NOT_SEGMENT matches nonmembership.",
      ),
    }).strict(),
    FeatureTargetingFilterSchema.extend({
      featureId: FeatureTargetingFilterSchema.shape.featureId.describe(
        "Internal feature ID of the referenced flag: use flag.featureId returned by flagTargetingGet, not flag.id or the flag key.",
      ),
      operator: FeatureTargetingFilterSchema.shape.operator.describe(
        "FLAG matches when the referenced flag evaluates to true in the same context; NOT_FLAG matches when it evaluates to false.",
      ),
    }).strict(),
  ])
  .superRefine(operatorRefinement);

// The flag editor and API validation share the same clause-type allowlist.
export const SupportedFlagFilterTypes: UIFilterType[] =
  SingleFlagFilterSchema.options.map((schema) => schema.shape.type.value);

// Keep the internal filter type for persisted rules, but only allow authoring
// a single clause or one flat AND group that the flag editor can edit.
export const FlagFilterSchema: z.ZodType<UIFilter, UIFilter> = z.union([
  SingleFlagFilterSchema,
  z.strictObject({
    type: z.literal("group"),
    operator: z.literal("and"),
    filters: z.array(SingleFlagFilterSchema),
  }),
]);

export type UserFilter = FilterTree<UserAttributeFilter>;
export const UserFilterGroupSchema = z.object({
  type: z.literal("group"),
  filters: z.lazy(() => z.array(UIUserFilterSchema)).optional(),
  operator: z.enum(["and", "or"]),
});

export const UIUserFilterSchema: z.ZodType<UserFilter> = z
  .discriminatedUnion("type", [
    UserAttributeFilterSchema,
    UserFilterGroupSchema,
  ])
  .superRefine(operatorRefinement);

export type RealtimeCompanyAttributeFilter = {
  type: "companyAttribute";
  field: string;
  operator: AttributeFilterOperator;
  values: string[];
  entityType: "company" | "user";
};

// Company-specific backend filters
export type CompanyRealtimeFilter = FilterTree<
  | RealtimeCompanyAttributeFilter
  | CompanyFeatureMetricFilter
  | FeatureFlagRolloutPercentageFilter
  | FilterConstant
>;

// Miscellaneous

// Empty AND group - typed to work with any FilterTree<T>
export const alwaysTrueFilter: FilterGroup<never> = {
  type: "group",
  operator: "and",
  filters: [],
};

export const alwaysFalseFilter: UIFilter = {
  type: "negation" as const,
  filter: alwaysTrueFilter,
};

export const customerSegmentFilter = createGroup("and", {
  type: "companyAttribute" as const,
  field: "plan",
  operator: "ANY_OF" as const,
  values: ["pro", "enterprise"],
});

export const betaSegmentFilter = {
  type: "companyAttribute",
  field: "beta",
  operator: "IS",
  values: ["true"],
} satisfies UIFilter;

export const allCompaniesSegmentFilter = (segment: SegmentDTO) =>
  ({
    type: "segment",
    operator: "SEGMENT",
    segmentId: segment.id,
  }) satisfies UIFilter;

export function createCompanyFilterFromOldAttributeFilter(
  oldAttributeFilter: OldAttributeFilter,
): FilterGroup<RealtimeCompanyAttributeFilter> {
  return {
    type: "group" as const,
    operator: "and" as const,
    filters: oldAttributeFilter.map((filter) => ({
      type: "companyAttribute" as const,
      field: filter.field,
      operator: filter.operator,
      values: filter.values,
      entityType: "company",
    })),
  };
}

export function getFilterList<T extends UIFilterType>(
  filter: UIFilter | null | undefined,
  types: T[],
): Extract<UIFilter, { type: T }>[] {
  if (!filter) return [];
  if (filter.type === "group" && filter.filters?.length) {
    return filter.filters.flatMap((f) => getFilterList(f, types));
  }
  return (types.includes(filter.type as T) ? [filter] : []) as Extract<
    UIFilter,
    { type: T }
  >[];
}

export function filterByType<T extends UIFilterType>(
  filter: UIFilter | null | undefined,
  types: T[],
): UIFilter | null {
  if (!filter) return null;
  if (filter.type === "group" && filter.filters?.length) {
    const filtered = filterMap(filter.filters, (f) => filterByType(f, types));
    if (!filtered.length) return null;
    return {
      ...filter,
      filters: filtered,
    };
  }
  return types.includes(filter.type as T) ? filter : null;
}

export function isAlwaysTrueFilter(
  filter: UIFilter | null | undefined,
): boolean {
  return (
    !!filter &&
    filter.type === "group" &&
    filter.operator === "and" &&
    !filter.filters?.length
  );
}

export function getFilterCount(filter: UIFilter): number {
  if (filter.type === "group") {
    return filter.filters?.reduce((acc, f) => acc + getFilterCount(f), 0) ?? 0;
  }
  return 1;
}

export function containsFeatureMetricFilter(companyFilter: UIFilter): boolean {
  return getFilterList(companyFilter, ["featureMetric"]).length > 0;
}

export function containsAttributeFilters(filter: UIFilter): boolean {
  if (filter.type === "group" && filter.filters?.length) {
    return filter.filters.some((f) => containsAttributeFilters(f));
  }

  if (filter.type === "companyAttribute") {
    return !systemAttributeFieldKeys.includes(filter.field);
  }

  if (filter.type === "userAttribute") {
    return !userSystemAttributeFieldKeys.includes(filter.field);
  }

  return filter.type === "otherContext";
}

export function operatorsByFilterType(type: UIFilterType): {
  [key in AttributeFilterType]: AttributeFilterOperator[];
} {
  switch (type) {
    case "companyAttribute":
    case "userAttribute":
    case "otherContext":
      return relevantOperators([
        "any",
        "date",
        "text",
        "number",
        "list",
        "boolean",
      ]);
    case "featureMetric":
      return relevantOperators([
        "any",
        "date",
        "text",
        "number",
        "list",
        "boolean",
      ]);
    case "segment":
      return relevantOperators(["segment"]);
    case "featureTargeting":
      return relevantOperators(["featureTargeting"]);
    default:
      return relevantOperators([]);
  }
}

export function getNewOperatorOnTypeChange(
  newType: UIFilterType,
  oldOperator: AttributeFilterOperator,
): AttributeFilterOperator {
  const allowedOperators = operatorsByFilterType(newType);
  const flatOperators = Object.values(allowedOperators).flat();

  return flatOperators.includes(oldOperator) ? oldOperator : flatOperators[0];
}

export function wrapWithFilterGroup<T extends FilterClass>(
  filter: T | T[] | null | undefined,
): FilterGroup<T> {
  if (!filter) {
    return alwaysTrueFilter;
  } else if (Array.isArray(filter)) {
    return {
      type: "group",
      operator: "and",
      filters: filter,
    };
  } else if (filter.type === "group") {
    return filter;
  } else {
    return {
      type: "group",
      operator: "and",
      filters: [filter],
    };
  }
}

export function createGroup<T extends FilterClass>(
  operator: "and" | "or",
  ...filters: (T | null | undefined)[]
): FilterGroup<T> {
  if (!filters.length) {
    return alwaysTrueFilter;
  }

  const actualFilters = filters.filter((f) => f) as T[];

  if (
    actualFilters.length == 1 &&
    actualFilters[0].type === "group" &&
    actualFilters[0].operator === operator
  ) {
    return actualFilters[0];
  }

  return {
    type: "group",
    operator,
    filters: actualFilters,
  };
}

export function reduce<T extends FilterClass>(
  filter: FilterTree<T> | FilterTree<T>[] | null | undefined,
): FilterTree<T> {
  // undefined and null are treated as empty filters
  if (!filter) {
    return alwaysTrueFilter;
  }

  // arrays are treated as AND groups
  if (Array.isArray(filter)) {
    return reduce<T>({ type: "group", operator: "and", filters: filter });
  }

  // negation is kept as is unless the reduction simplifies it to an empty filter
  // otherwise, we simplify the negated filter
  if (filter.type === "negation") {
    const simplified = reduce(filter.filter);
    return simplified.type === "group" && !simplified.filters?.length
      ? simplified
      : { ...filter, filter: simplified };
  }

  // groups are simplified by flattening them if possible
  if (filter.type === "group") {
    const reducedChildren = filter.filters?.map(reduce<T>) ?? [];

    if (reducedChildren.length === 0) {
      return alwaysTrueFilter;
    }
    if (reducedChildren.length === 1) {
      return reducedChildren[0];
    }

    const canFlatten = reducedChildren.every(
      (f) =>
        (f.type === "group" && f.operator === filter.operator) ||
        f.type !== "group",
    );

    if (canFlatten) {
      return {
        ...filter,
        filters: reducedChildren.flatMap((f) =>
          f.type === "group" ? f.filters! : [f],
        ),
      };
    }

    return { ...filter, filters: reducedChildren };
  }

  return filter;
}

export type Dependee = Omit<DependentEntity, "filter" | "name" | "key">;

export type DependentEntity = {
  id: string;
  name: string;
  filter: UIFilter;
  type: "segment" | "feature";
} & (
  | {
      type: "segment";
    }
  | { type: "feature"; icon: string | null; iconColor: string | null }
);

const cycleDepthLimit = 10;

export class FiltersError extends APIHandlerError {
  constructor(message: string, path: PropertyKey[], notPossible = false) {
    super(notPossible ? 409 : 400, {
      error: {
        message: message,
        code: notPossible ? "not_possible" : "invalid_request",
      },
      issues: {
        [path.join(".")]: [message],
      },
    });

    this.name = "FiltersError";
  }
}

export function getDependentEntities(
  dependee: Dependee,
  dependencies: Record<string, DependentEntity>,
): DependentEntity[] {
  const result: DependentEntity[] = [];

  function unrollFilterRecursive(filter: UIFilter, path: DependentEntity[]) {
    if (path.length > cycleDepthLimit) {
      throw new FiltersError(
        "Circular reference detected",
        path.map((p) => `${p.type}[${p.id}]`),
        true,
      );
    }

    if (filter.type === "segment") {
      if (filter.segmentId === dependee.id) {
        result.push(...path);
      }

      const segment = dependencies[filter.segmentId];
      if (segment) {
        unrollFilterRecursive(segment.filter, [...path, segment]);
      }
    } else if (filter.type === "featureTargeting") {
      if (filter.featureId === dependee.id) {
        result.push(...path);
      }

      const featureTargeting = dependencies[filter.featureId];
      if (featureTargeting) {
        unrollFilterRecursive(featureTargeting.filter, [
          ...path,
          featureTargeting,
        ]);
      }
    } else if (filter.type === "featureMetric") {
      if (filter.featureId === dependee.id) {
        result.push(...path);
      }

      const feature = dependencies[filter.featureId];
      if (feature) {
        unrollFilterRecursive(feature.filter, [...path, feature]);
      }
    } else if (filter.type === "group" && filter.filters?.length) {
      filter.filters.flat().forEach((f) => unrollFilterRecursive(f, path));
    }
  }

  Object.values(dependencies)
    .flat()
    .forEach((dep) => unrollFilterRecursive(dep.filter, [dep]));

  return uniqBy(
    result.filter((dep) => dep.id != dependee.id),
    (d) => d.id,
  );
}

const nonSimpleAttributeFields = [
  FIRST_SEEN_FIELD_KEY,
  LAST_SEEN_FIELD_KEY,
  USER_COUNT_FIELD_KEY,
];

type GenericFilter =
  | {
      type: Exclude<
        CompanyRealtimeFilter["type"] | UIFilter["type"],
        | "group"
        | "companyAttribute"
        | "userAttribute"
        | "segment"
        | "featureTargeting"
      >;
    }
  | {
      type: "companyAttribute" | "userAttribute";
      field: string;
    }
  | {
      type: "segment";
      segmentId: string;
    }
  | {
      type: "featureTargeting";
      featureId: string;
    }
  | { type: "group"; filters?: GenericFilter[] };

export function isStatelessFilter(
  filter: GenericFilter,
  dependencies?: Record<string, DependentEntity>,
  path: (string | number)[] = [],
): boolean {
  if (!filter) {
    return true;
  }

  if (path.length > cycleDepthLimit) {
    throw new FiltersError("Circular reference detected", path, true);
  }

  if (filter.type === "companyAttribute" || filter.type === "userAttribute") {
    return !nonSimpleAttributeFields.includes(filter.field);
  } else if (filter.type === "segment") {
    const segment = dependencies?.[filter.segmentId];
    if (segment) {
      return isStatelessFilter(segment.filter, dependencies, [
        ...path,
        `segment[${segment.id}]`,
      ]);
    }
  } else if (filter.type === "featureTargeting") {
    const featureTargeting = dependencies?.[filter.featureId];
    if (featureTargeting) {
      return isStatelessFilter(featureTargeting.filter, dependencies, [
        ...path,
        `featureTargeting[${featureTargeting.id}]`,
      ]);
    }
  } else if (filter.type === "group") {
    if (!filter.filters?.length) {
      return true;
    }

    path = [...path, `group`];

    return filter.filters.every((f, index) =>
      isStatelessFilter(f, dependencies, [...path, index]),
    );
  }

  return filter.type !== "featureMetric";
}

const funnelStepToIndex = FunnelStepList.reduce(
  (acc, cur) => {
    acc[cur] = FunnelStepList.indexOf(cur);
    return acc;
  },
  {} as Record<FunnelStep, number>,
);

function mapFunnelStepsToValues(steps: FunnelStep[]) {
  return steps.map((s) => funnelStepToIndex[s].toString());
}

export function buildFeatureFunnelFilters(
  step: FunnelStep | "satisfied",
  featureId: string,
  useTargeting: boolean,
  segments?: string[],
) {
  let exclFilter: UIFilter;
  let inclFilter: UIFilter;

  const starsFilterBase = {
    type: "featureMetric" as const,
    featureId: featureId,
    operator: "ANY_OF" as const,
    metric: "funnelStep" as const,
  };

  switch (step) {
    case "company":
      return [alwaysTrueFilter, alwaysTrueFilter];
    case "segment":
      exclFilter = alwaysTrueFilter;
      inclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues([
          "company",
          "segment",
          "tried",
          "adopted",
          "retained",
        ]),
      };

      break;
    case "tried":
      exclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues(["company"]),
      };
      inclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues([
          "segment",
          "tried",
          "adopted",
          "retained",
        ]),
      };

      break;
    case "adopted":
      exclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues(["tried"]),
      };
      inclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues(["adopted", "retained"]),
      };
      break;
    case "retained":
      exclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues(["adopted"]),
      };
      inclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues(["retained"]),
      };
      break;
    case "satisfied":
      exclFilter = {
        ...starsFilterBase,
        values: mapFunnelStepsToValues(["retained"]),
      };

      inclFilter = {
        type: "group",
        operator: "and",
        filters: [
          {
            ...starsFilterBase,
            values: mapFunnelStepsToValues(["retained"]),
          },
          {
            type: "featureMetric",
            featureId: featureId,
            operator: "GT",
            metric: "satisfaction",
            values: ["3"],
          },
        ],
      };

      break;
  }

  if (segments?.length) {
    const subsegmentFilters = segments.map(
      (s) =>
        ({
          type: "segment",
          operator: "SEGMENT",
          segmentId: s,
        }) satisfies UIFilter,
    );

    exclFilter = {
      type: "group",
      operator: "and",
      filters: [exclFilter, ...subsegmentFilters],
    };

    inclFilter = {
      type: "group",
      operator: "and",
      filters: [inclFilter, ...subsegmentFilters],
    };
  }

  if (useTargeting) {
    const targetingFilter = {
      type: "featureTargeting" as const,
      featureId: featureId,
      operator: "FLAG" as const,
    };

    exclFilter = {
      type: "group",
      operator: "and",
      filters: [exclFilter, targetingFilter],
    };

    inclFilter = {
      type: "group",
      operator: "and",
      filters: [inclFilter, targetingFilter],
    };
  }
  return [reduce(exclFilter), reduce(inclFilter)];
}

export type RuleWithPartialRollout = {
  partialRolloutThreshold: number;
  partialRolloutContextAttribute: string;
  filter: UIFilter;
};

export function compileRules(
  targeting: z.output<typeof FeatureTargetingSchema> | undefined | null,
  ignoreMode = false,
): RuleWithPartialRollout[] {
  if (!targeting) {
    return [];
  } else if (!ignoreMode) {
    if (targeting.targetingMode === "everyone") {
      return [
        {
          partialRolloutThreshold: MAX_ROLLOUT_THRESHOLD,
          partialRolloutContextAttribute: COMPANY_ID_CONTEXT_FIELD,
          filter: alwaysTrueFilter,
        },
      ];
    } else if (targeting.targetingMode === "none") {
      return [];
    }
  }
  const basicFilters: UIFilter[] = [
    targeting.userIds.length
      ? ({
          type: "userAttribute",
          operator: "ANY_OF",
          field: "id",
          values: targeting.userIds,
        } satisfies UIFilter)
      : undefined,
    targeting.companyIds.length
      ? ({
          type: "companyAttribute",
          operator: "ANY_OF",
          field: "$company_id",
          values: targeting.companyIds,
        } satisfies UIFilter)
      : undefined,
    targeting.segmentIds.length
      ? (reduce(
          createGroup(
            "or",
            ...targeting.segmentIds.map(
              (segmentId) =>
                ({
                  type: "segment",
                  operator: "SEGMENT",
                  segmentId,
                }) satisfies UIFilter,
            ),
          ),
        ) satisfies UIFilter)
      : undefined,
  ].filter((f) => f !== undefined);

  return [
    ...targeting.customRules,
    ...basicFilters.map((filter) => ({
      filter,
      partialRolloutThreshold: MAX_ROLLOUT_THRESHOLD,
      partialRolloutContextAttribute: COMPANY_ID_CONTEXT_FIELD,
    })),
  ].filter(({ filter }) => getFilterCount(filter));
}
